import { CheckboxField } from '@ovComponents/2-component/checkboxField/checkboxField';
import { TableCell, TableRow } from '@ovComponents/2-component/table/table';
import { formatMoneyValue } from '@utils/moneyFormatting';
import type { Theme } from 'hephaestus/core';
import { Box, IconButton, Tooltip, Typography, useTheme } from 'hephaestus/web';
import {
  Block as BlockIcon,
  CheckCircleOutlined as CheckCircleOutlineIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from 'hephaestus/webIcons';
import { useCallback, useState } from 'react';
import {
  LABEL_SX,
  MICRO_SX,
  SECONDARY_TEXT_SX,
  SMALL_BOLD_SX,
  SMALL_SX,
  TRUNCATE_SX,
} from '../styles';
import { isBuyType, isSellType, tradeSideColor } from '../utils';
import EditableTableCell from './editableTableCell';
import TradeRowDetail from './tradeRowDetail';

const ADVISOR_STATES: Record<
  string,
  {
    label: string;
    badge: 'warning' | 'positive' | 'supportOne' | 'neutral' | 'negative';
    description: string;
  }
> = {
  INITIATED: {
    label: 'Pending',
    badge: 'warning',
    description: 'Awaiting verification or batch eligibility',
  },
  READY: {
    label: 'Ready',
    badge: 'positive',
    description: 'Eligible for next trade batch',
  },
  REQUESTED: {
    label: 'Sent',
    badge: 'supportOne',
    description: 'Submitted to broker, awaiting fill',
  },
  RECONCILED: {
    label: 'Filled',
    badge: 'neutral',
    description: 'Executed and settled',
  },
  CANCELED: {
    label: 'Canceled',
    badge: 'negative',
    description: 'Trade withdrawn',
  },
};

const LOCKED_STATES = new Set(['REQUESTED', 'RECONCILED', 'CANCELED']);

const ActionButton = ({
  label,
  active,
  color,
  onClick,
  theme,
}: {
  label: string;
  active?: boolean;
  color: 'success' | 'error';
  onClick: (e: React.MouseEvent) => void;
  theme: Theme;
}) => {
  const accent =
    color === 'success' ? theme.color.positive : theme.color.negative;
  const accentVariant =
    color === 'success'
      ? theme.color.positiveVariant
      : theme.color.negativeVariant;
  return (
    <Box
      onClick={onClick}
      sx={{
        px: 0.75,
        py: '3px',
        cursor: 'pointer',
        borderRadius: '4px',
        border: '1px solid',
        borderColor: active ? accent : theme.color.outlineVariant,
        bgcolor: active ? accentVariant : 'transparent',
        transition: 'all 0.12s',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.4,
        whiteSpace: 'nowrap',
        '&:hover': {
          borderColor: accent,
          bgcolor: accentVariant,
        },
      }}
    >
      {active && color === 'success' && (
        <CheckCircleOutlineIcon sx={{ fontSize: 11, color: accent }} />
      )}
      <Typography
        variant='labelSmall'
        sx={{
          ...LABEL_SX,
          fontWeight: 700,
          color: active ? accent : theme.color.onSurfaceVariant,
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const TradeRequestRow = ({
  trade,
  selected,
  onToggleSelect,
  onCancel,
  onVerify,
  onUpdateAmount,
}: {
  trade: any;
  selected?: boolean;
  onToggleSelect?: () => void;
  onCancel?: (id: string) => void;
  onVerify?: (id: string) => void;
  onUpdateAmount?: (
    id: string,
    newAmountCents: number,
    currentPriceCents?: number,
  ) => void;
  isNew?: boolean;
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const stateInfo = ADVISOR_STATES[trade.state] || {
    label: trade.state,
    badge: 'neutral' as const,
    description: '',
  };
  const isBuy = isBuyType(trade.type);
  const isSell = isSellType(trade.type, trade.isSellAll);
  const clientName = trade.subAccount?.account?.user?.firstName || 'Unknown';
  const clientLastName = trade.subAccount?.account?.user?.lastName || '';
  const accountType = trade.subAccount?.account?.type || '—';
  const displayAmount = Math.abs(trade.moneyAllocatedCents || 0);
  const quantity = trade.financialProduct?.currentPriceCents
    ? Math.abs(displayAmount) / trade.financialProduct.currentPriceCents
    : 0;
  const daysToSettle = trade.settlementDate
    ? Math.ceil(
        (new Date(trade.settlementDate).getTime() - Date.now()) / 86400000,
      )
    : null;
  const settleUrgent = daysToSettle !== null && daysToSettle <= 0;
  const settleWarning =
    daysToSettle !== null && daysToSettle > 0 && daysToSettle <= 2;
  const hasSchedule = !!trade.schedule;
  const scheduleNextDate = trade.schedule?.nextDate;
  const isScheduleBlocked =
    hasSchedule && scheduleNextDate && new Date(scheduleNextDate) > new Date();
  const isBlocked =
    trade.blockedUntilAccountActive ||
    trade.blockedUntilSubAccountActive ||
    trade.blockedUntil ||
    isScheduleBlocked;
  const blockedReasons: string[] = [];
  if (trade.blockedUntilAccountActive)
    blockedReasons.push('Account is not active/approved');
  if (trade.blockedUntilSubAccountActive)
    blockedReasons.push('Sub-account is not active/approved');
  if (trade.blockedUntil)
    blockedReasons.push(
      `Blocked until ${new Date(trade.blockedUntil).toLocaleDateString()}`,
    );
  if (isScheduleBlocked)
    blockedReasons.push(
      `Illiquid — next trade date: ${new Date(scheduleNextDate).toLocaleDateString()} (${trade.schedule.name})`,
    );

  const isLocked = LOCKED_STATES.has(trade.state);
  const isInBatch = !!trade.bulkTradeRun && trade.state !== 'REQUESTED';
  const isRequested = trade.state === 'REQUESTED';

  const handleAmountSave = useCallback(
    (newValue: number) => {
      if (onUpdateAmount && trade.state === 'INITIATED' && !isInBatch) {
        const newCents = Math.round(Math.abs(newValue));
        if (newCents !== displayAmount) {
          onUpdateAmount(
            trade.id,
            newCents,
            trade.financialProduct?.currentPriceCents,
          );
        }
      }
    },
    [
      onUpdateAmount,
      trade.id,
      trade.state,
      displayAmount,
      trade.financialProduct?.currentPriceCents,
      isInBatch,
    ],
  );
  const isActionable = !isLocked && !isInBatch;
  const settleSx = {
    color: settleUrgent
      ? theme.color.negative
      : settleWarning
        ? theme.color.warning
        : theme.color.onSurfaceVariant,
    fontWeight: settleUrgent || settleWarning ? 700 : 400,
  };

  return (
    <>
      <TableRow
        sx={{
          bgcolor: isBuy
            ? 'success.light'
            : isSell
              ? 'error.light'
              : 'transparent',
          '&:hover': {
            bgcolor: isBuy
              ? 'success.light'
              : isSell
                ? 'error.light'
                : 'action.hover',
          },
          borderLeft: '3px solid',
          borderLeftColor: isBuy
            ? 'success.main'
            : isSell
              ? 'error.main'
              : 'transparent',
          ...(isLocked || isInBatch ? { opacity: 0.7 } : {}),
        }}
      >
        {onToggleSelect && (
          <TableCell width='3%' sx={{ py: 0.5 }}>
            <CheckboxField
              checked={!!selected}
              onChange={() => onToggleSelect()}
              disabled={isLocked || isInBatch}
              customStyle={{
                p: 0,
                ...(isLocked || isInBatch ? { opacity: 0.3 } : {}),
              }}
            />
          </TableCell>
        )}
        <TableCell width={onToggleSelect ? '3%' : '4%'} sx={{ py: 0.5 }}>
          <Tooltip title={expanded ? 'Collapse' : 'Expand'}>
            <IconButton
              onClick={() => setExpanded(!expanded)}
              size='xsmall'
              aria-label={expanded ? 'Collapse' : 'Expand'}
            >
              {expanded ? (
                <KeyboardArrowUpIcon sx={{ fontSize: 16 }} fontSize='inherit' />
              ) : (
                <KeyboardArrowDownIcon
                  sx={{ fontSize: 16 }}
                  fontSize='inherit'
                />
              )}
            </IconButton>
          </Tooltip>
        </TableCell>
        <TableCell width='15%' sx={{ py: 0.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Typography variant='bodySmall' sx={{ fontWeight: 600 }}>
              {trade.financialProduct?.ticker || 'Unknown'}
            </Typography>
            {isBlocked &&
              (() => {
                const isHardBlock =
                  trade.blockedUntilAccountActive ||
                  trade.blockedUntilSubAccountActive;
                let badgeColor: any;
                let badgeBg: any;
                let badgeLabel: string;
                if (isHardBlock) {
                  badgeColor = theme.color.negative;
                  badgeBg = theme.color.negativeVariant;
                  badgeLabel = 'BLOCKED';
                } else if (isScheduleBlocked) {
                  badgeColor = theme.color.warning;
                  badgeBg = theme.color.warningVariant;
                  badgeLabel = 'SCHEDULED';
                } else {
                  badgeColor = theme.color.warning;
                  badgeBg = theme.color.warningVariant;
                  badgeLabel = 'BLOCKED';
                }
                return (
                  <Tooltip title={blockedReasons.join(' · ')} placement='top'>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 0.5,
                        py: '1px',
                        borderRadius: '4px',
                        bgcolor: badgeBg,
                        color: badgeColor,
                        cursor: 'help',
                      }}
                    >
                      <BlockIcon sx={{ fontSize: 11, mr: 0.25 }} />
                      <Typography
                        variant='labelSmall'
                        sx={{
                          ...MICRO_SX,
                          fontWeight: 700,
                          lineHeight: 1,
                          color: 'inherit',
                        }}
                      >
                        {badgeLabel}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })()}
          </Box>
          <Typography
            variant='bodySmall'
            sx={{ ...SECONDARY_TEXT_SX, ...TRUNCATE_SX }}
          >
            {trade.financialProduct?.translatedName?.en || ''}
          </Typography>
        </TableCell>
        <TableCell width='5%' number sx={{ py: 0.5 }}>
          <Typography
            variant='bodySmall'
            sx={{ fontWeight: 600, color: tradeSideColor(theme.color, isBuy) }}
          >
            {isBuy ? 'BUY' : 'SELL'}
          </Typography>
        </TableCell>
        <TableCell width='4%' sx={{ py: 0.5 }}>
          <Typography
            variant='labelSmall'
            sx={{
              ...SMALL_BOLD_SX,
              fontWeight: 700,
              letterSpacing: '0.03em',
              color: theme.color.onSurfaceVariant,
            }}
          >
            {trade.currency || '—'}
          </Typography>
        </TableCell>
        <TableCell width='7%' number sx={{ py: 0.5 }}>
          <Typography variant='bodySmall'>{quantity.toFixed(2)}</Typography>
          {trade.isSellAll && (
            <Typography
              variant='bodySmall'
              component='div'
              sx={{ color: theme.color.warning, fontWeight: 700 }}
            >
              SELL ALL
            </Typography>
          )}
        </TableCell>
        {onUpdateAmount && !isLocked ? (
          <EditableTableCell
            value={displayAmount}
            onSave={handleAmountSave}
            formatDisplay={(v) => formatMoneyValue(Number(v), '$', 2)}
            editable={trade.state === 'INITIATED' && !isInBatch}
            number
            width='11%'
            sx={{ py: 0.5 }}
          />
        ) : (
          <TableCell width='11%' number sx={{ py: 0.5 }}>
            <Typography variant='bodySmall' sx={{ fontWeight: 600 }}>
              {formatMoneyValue(displayAmount, '$', 2)}
            </Typography>
          </TableCell>
        )}
        <TableCell width='13%' sx={{ py: 0.5 }}>
          <Typography variant='bodySmall' component='div'>
            {clientName}
            {clientLastName ? ` ${clientLastName}` : ''}
          </Typography>
          <Typography
            variant='bodySmall'
            component='div'
            sx={{ ...SECONDARY_TEXT_SX }}
          >
            {accountType}
          </Typography>
        </TableCell>
        <TableCell width='7%' sx={{ py: 0.5 }}>
          <Typography variant='bodySmall' sx={settleSx}>
            {trade.settlementDate
              ? new Date(trade.settlementDate).toLocaleDateString()
              : '—'}
          </Typography>
          {daysToSettle !== null && (
            <Typography
              variant='labelSmall'
              component='div'
              sx={{
                color: settleUrgent
                  ? theme.color.negative
                  : settleWarning
                    ? theme.color.warning
                    : theme.color.onSurfaceVariant,
              }}
            >
              T{daysToSettle >= 0 ? '+' : ''}
              {daysToSettle}
            </Typography>
          )}
        </TableCell>
        <TableCell width='12%' sx={{ py: 0.5 }}>
          {isRequested ? (
            <Typography
              variant='labelSmall'
              sx={{
                px: 0.75,
                py: '2px',
                borderRadius: '4px',
                bgcolor: theme.color.positiveVariant,
                color: theme.color.positive,
                ...SMALL_BOLD_SX,
                fontWeight: 700,
                display: 'inline-block',
              }}
            >
              Requested
            </Typography>
          ) : isInBatch ? (
            <Typography
              variant='labelSmall'
              sx={{
                px: 0.75,
                py: '2px',
                borderRadius: '4px',
                bgcolor: theme.color.primaryVariant,
                color: theme.color.primary,
                ...SMALL_BOLD_SX,
                fontWeight: 700,
                display: 'inline-block',
              }}
            >
              In Batch
            </Typography>
          ) : isActionable ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                flexWrap: 'nowrap',
              }}
            >
              {onVerify && (
                <ActionButton
                  label={trade.verifiedAt ? 'Verified' : 'Verify'}
                  active={!!trade.verifiedAt}
                  color='success'
                  onClick={(e) => {
                    e.stopPropagation();
                    onVerify(trade.id);
                  }}
                  theme={theme}
                />
              )}
              {onCancel && (
                <ActionButton
                  label='Cancel'
                  color='error'
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancel(trade.id);
                  }}
                  theme={theme}
                />
              )}
            </Box>
          ) : (
            <Typography
              variant='labelSmall'
              sx={{ ...SMALL_SX, color: theme.color.onDisabled }}
            >
              —
            </Typography>
          )}
        </TableCell>
      </TableRow>

      {expanded && (
        <TradeRowDetail
          trade={trade}
          colSpan={onToggleSelect ? 10 : 9}
          stateInfo={stateInfo}
          isBuy={isBuy}
          isBlocked={isBlocked}
        />
      )}
    </>
  );
};

export default TradeRequestRow;
